/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import {
  Icon,
  LinkVariant,
  IconVariant,
} from '@gousto-internal/citrus-react'
import { createSlugFromUrl } from 'src/utils'
import { Link } from '../Routing/Link'
import css from './Breadcrumbs.module.css'
import type { Category, Section } from '../../types/entities'
import { client } from '../../config/routes'

type RenderLink = {
  label: string;
  to: string;
}

const rootBreadcrumb = {
  label: 'Help Centre',
  to: `/help-centre${client.help}`,
}

type BreadcrumbsProps = {
  categories?: Category[],
  currentCategoryId?: string,
  sections?: Section[],
  currentSectionId?: string,
}

const Breadcrumbs = ({
  categories,
  currentCategoryId,
  sections,
  currentSectionId,
}: BreadcrumbsProps) => {
  const [items, setItems] = useState([rootBreadcrumb])
  const category = useMemo(
    () => categories?.find((c) => c.id.toString() === currentCategoryId),
    [categories, currentCategoryId],
  )
  const section = useMemo(
    () => sections?.find((s) => s.id.toString() === currentSectionId),
    [sections, currentSectionId],
  )

  useEffect(() => {
    const breadcrumbsBits = []
    if (category) {
      const categorySlug = createSlugFromUrl(category.htmlURL)
      breadcrumbsBits.push({
        label: category.name,
        to: `/help-centre${client.categories}/${categorySlug}/${category.id}`,
      })

      if (section) {
        const sectionSlug = createSlugFromUrl(section.htmlURL)
        breadcrumbsBits.push({
          label: section.name,
          to: `/help-centre${client.categories}/${section.categoryId}${client.sections}/${sectionSlug}/${section.id}`,
        })
      }
    }
    setItems([rootBreadcrumb, ...breadcrumbsBits])
  }, [category, section])

  return (
    <ul className={css.breadcrumbsList}>
      {items.map((item: RenderLink, index) => (
        <Link
          href={item.to}
          variant={LinkVariant.None}
          key={item.to}
          css={{ textDecoration: 'none' }}
        >
          <li>{item.label}</li>
          {index !== items.length - 1 && (
            <Icon name="chevron_right" variant={IconVariant.Inherit} />
          )}
        </Link>
      ))}
    </ul>
  )
}

export { Breadcrumbs }
